// Line Execute — Dispatcher.
//
// Bridges a detected intent to the unified action registry and normalizes
// the result. The dispatcher is intentionally thin: all decisions are pushed
// into the intent detector (do we route?) or the unified ActionDefinition
// (validation, permission, quota, and execution).

import type { AiOperationTargetRef } from '../../../types/aiOperational'
import type { AiRetrievalElement } from '../context/operationalRetrieval'
import type { ChecklistItem } from '../../../types/schema'
import type { WorkspaceAction } from '../../../utils/workspacePermissions'
import type { ModuleKey } from '../../../utils/workspaceModules'
import type { ActionContext, ActionOutcome } from '../../actions/action'
import { getAction, invokeAction } from '../../actions/registry'
import { detectLineExecuteIntent } from './intentDetector'
import { detectWebIntent } from '../intents/webIntentMatcher'
import { WEB_INTENT_CATALOG, resolveWebIntentAvailability, type WebIntentDefinition } from '../intents/webIntentCatalog'
import { LINE_EXECUTE_KIND_TO_ACTION_ID, type LineExecuteActionKind } from './lineExecuteKindMap'

export interface LineExecuteActor {
  uid: string
  role?: string
  displayName?: string | null
}

// Shared context for AI Execute dispatch.
export interface LineExecuteContext {
  workspaceId: string
  workspaceSlug?: string | null
  actor: LineExecuteActor
  prompt: string
  displayPrompt: string
  timezone: string
  boardElements: AiRetrievalElement[]
  checklistItems: ChecklistItem[]
  selectedTargets: AiOperationTargetRef[]
  webIntents?: WebIntentDefinition[]
  canPerform: (permission: WorkspaceAction) => boolean
  showToast?: (message: string, level?: 'success' | 'error' | 'info' | 'warning') => void
  navigate?: (path: string) => void
  switchedOffModules?: ReadonlySet<ModuleKey>
}

// Detector output.
export interface LineExecuteIntentMatch {
  kind: LineExecuteActionKind
  confidence: number
  slots: Record<string, unknown>
  matchedText: string
  reason: string
  catalogIntentId?: string
}

// Execution outcome envelope for AI chat turns.
export type LineExecuteOutcome =
  | { kind: 'COMPLETED'; message: string; previewText?: string; toast?: { level: 'success' | 'info'; message: string }; navigateTo?: string }
  | { kind: 'NEEDS_CONFIRMATION'; message: string; confirmPayload: unknown; quickControls: Array<{ label: string; value: string }> }
  | { kind: 'FAILED'; message: string; recoverable: boolean }
  | { kind: 'BLOCKED_BY_PERMISSION'; message: string; missingPermission: WorkspaceAction }
  | { kind: 'BLOCKED_BY_QUOTA'; message: string; quotaKind: string }

export type LineExecuteDispatchResult =
  | { kind: 'NO_MATCH' }
  | { kind: 'ADAPTER_MISSING'; intent: LineExecuteIntentMatch }
  | { kind: 'DISPATCHED'; intent: LineExecuteIntentMatch; outcome: LineExecuteOutcome }
  | { kind: 'ADAPTER_ERROR'; intent: LineExecuteIntentMatch; error: string }

const STATUS_PATTERNS = [
  /\bdone|completed?\b/i,
  /\bin\s*progress\b/i,
  /\btodo|to[- ]do\b/i,
  /\bblocked\b/i,
  /\breview\b/i,
]

const findTimelineTargetsByNameFragment = (fragment: string, context: LineExecuteContext) => {
  const needle = fragment.trim().toLowerCase()
  if (!needle) return []
  return context.boardElements
    .filter((element) => (element.title ?? '').toLowerCase().includes(needle))
    .slice(0, 5)
    .map((element) => ({ id: element.id, title: element.title ?? element.id, type: element.type }))
}

const nameFragmentForTimeline = (prompt: string): string => {
  const statusPattern = STATUS_PATTERNS.find((pattern) => pattern.test(prompt))
  return prompt
    .replace(/\b(mark|set|move|change)\b/i, '')
    .replace(statusPattern ?? /$^/, '')
    .replace(/\bto\b|\bas\b/gi, '')
    .trim()
}

const findChecklistByFragment = (fragment: string, context: LineExecuteContext) => {
  const needle = fragment.trim().toLowerCase()
  if (!needle) return []
  return context.checklistItems
    .filter((item) => (item.title ?? '').toLowerCase().includes(needle))
    .slice(0, 5)
    .map((item) => ({ id: item.id, title: item.title ?? item.id }))
}

const nameFragmentForChecklist = (prompt: string): string => prompt
  .replace(/\b(check\s+off|tick|uncheck|untick|reopen|mark|done)\b/gi, '')
  .replace(/\b(the|item|checklist)\b/gi, '')
  .replace(/[.!?,;:]+$/g, '')
  .trim()

const QUICK_CONTROLS_BY_KIND: Partial<Record<LineExecuteActionKind, Array<{ label: string; value: string }>>> = {
  MEETING_SCHEDULE: [
    { label: 'Schedule it', value: 'Yes, schedule this meeting.' },
    { label: 'Change time', value: 'Change the time.' },
    { label: 'Cancel', value: 'Never mind.' },
  ],
  MEETING_CANCEL: [
    { label: 'Cancel meeting', value: 'Yes, cancel this meeting.' },
    { label: 'Keep it', value: 'No, keep it.' },
  ],
  TIMELINE_STATUS_CHANGE: [
    { label: 'Move to Done', value: 'Yes, move it.' },
    { label: 'Cancel', value: 'Never mind.' },
  ],
  CHECKLIST_TOGGLE_DONE: [
    { label: 'Complete them', value: 'Yes, complete them.' },
    { label: 'Cancel', value: 'Never mind.' },
  ],
  CHAT_SEND: [
    { label: 'Post it', value: 'Yes, post it.' },
    { label: 'Cancel', value: 'Never mind.' },
  ],
  MEMBER_INVITE: [
    { label: 'Send invite', value: 'Yes, send the invite.' },
    { label: 'Cancel', value: 'Never mind.' },
  ],
}

const KIND_TO_PERMISSION: Partial<Record<LineExecuteActionKind, WorkspaceAction>> = {
  MEETING_SCHEDULE: 'meetings.schedule',
  MEETING_CANCEL: 'meetings.manage',
  TIMELINE_STATUS_CHANGE: 'timeline.edit',
  CHECKLIST_TOGGLE_DONE: 'checklist.change_status',
  CHAT_SEND: 'chat.send',
  MEMBER_INVITE: 'workspace.manage_invites',
  REPORT_PREVIEW: 'workspace.view_reports',
  FORM_SUBMIT: 'forms.submit',
}

const buildActionArgs = (intent: LineExecuteIntentMatch, context: LineExecuteContext): unknown => {
  switch (intent.kind) {
    case 'MEETING_SCHEDULE':
      return {
        prompt: context.prompt,
        timezone: context.timezone || 'UTC',
      }
    case 'MEETING_CANCEL': {
      const targets = context.selectedTargets
        .filter((target) => target.type === 'MEETING')
        .map((target) => ({ id: target.id, title: target.title ?? target.id }))
      return { targets }
    }
    case 'TIMELINE_STATUS_CHANGE': {
      const selectedTargets = context.selectedTargets.slice(0, 5).map((target) => ({
        id: target.id,
        title: target.title ?? target.id,
        type: target.type,
      }))
      return {
        prompt: context.prompt,
        selectedTargets,
        nameCandidates: selectedTargets.length === 0
          ? findTimelineTargetsByNameFragment(nameFragmentForTimeline(context.prompt), context)
          : [],
      }
    }
    case 'CHECKLIST_TOGGLE_DONE': {
      const selectedChecklistTargets = context.selectedTargets
        .filter((target) => target.type === 'CHECKLIST')
        .slice(0, 5)
        .map((target) => ({ id: target.id, title: target.title ?? target.id }))
      return {
        prompt: context.prompt,
        selectedChecklistTargets,
        checklistCandidates: findChecklistByFragment(nameFragmentForChecklist(context.prompt), context),
      }
    }
    case 'CHAT_SEND':
      return { prompt: context.prompt }
    case 'REPORT_PREVIEW':
      return {
        prompt: context.prompt,
        selectedTargetCount: context.selectedTargets.length,
        boardElementCount: context.boardElements.length,
        checklistItemCount: context.checklistItems.length,
      }
    case 'MEMBER_INVITE':
      return { prompt: context.prompt }
    case 'NAVIGATION': {
      const targetRoute = intent.slots.targetRoute
      const target = intent.matchedText.split(/\s+/).slice(-1)[0] ?? ''
      return {
        target,
        targetLabel: intent.matchedText || 'that item',
        targetRoute: typeof targetRoute === 'string' ? targetRoute : undefined,
        workspaceSlug: context.workspaceSlug ?? undefined,
      }
    }
    case 'EXPLORE_OPEN_PREVIEW':
      return { intentText: intent.matchedText }
    case 'FORM_SUBMIT':
      return {
        prompt: context.prompt,
        formId: typeof intent.slots.formId === 'string' ? intent.slots.formId : undefined,
        responseId: typeof intent.slots.responseId === 'string' ? intent.slots.responseId : undefined,
        title: typeof intent.slots.title === 'string' ? intent.slots.title : undefined,
        ...intent.slots,
      }
    default:
      return { prompt: context.prompt, ...intent.slots }
  }
}

const toActionContext = (intent: LineExecuteIntentMatch, context: LineExecuteContext): ActionContext => ({
  workspaceId: context.workspaceId,
  actor: context.actor,
  canPerform: context.canPerform,
  showToast: context.showToast,
  navigate: context.navigate,
  boardElements: context.boardElements,
  checklistItems: context.checklistItems,
  selectedTargets: context.selectedTargets,
  timezone: context.timezone,
  invokedFrom: 'ai',
  ai: { matchedText: intent.matchedText, catalogIntentId: intent.catalogIntentId, prompt: context.prompt },
  switchedOffModules: context.switchedOffModules,
})

const toLineExecuteOutcome = (
  kind: LineExecuteActionKind,
  outcome: ActionOutcome<unknown>,
): LineExecuteOutcome => {
  switch (outcome.kind) {
    case 'COMPLETED': {
      const resultObj = typeof outcome.result === 'object' && outcome.result !== null ? outcome.result as Record<string, unknown> : null
      const toastMessage = typeof resultObj?.toastMessage === 'string' ? resultObj.toastMessage : undefined
      const navigateTo = outcome.navigateTo ?? (typeof resultObj?.navigateTo === 'string' ? resultObj.navigateTo : undefined)
      const toast = toastMessage ? { level: 'info' as const, message: toastMessage } : undefined
      return {
        kind: 'COMPLETED',
        message: outcome.message ?? 'Done.',
        navigateTo,
        toast,
      }
    }
    case 'FAILED':
      return {
        kind: 'FAILED',
        message: outcome.message,
        recoverable: outcome.recoverable,
      }
    case 'BLOCKED_BY_PERMISSION': {
      const missingPermission = (KIND_TO_PERMISSION[kind] ?? outcome.missingPermission) as WorkspaceAction
      return {
        kind: 'BLOCKED_BY_PERMISSION',
        message: outcome.message,
        missingPermission,
      }
    }
    case 'BLOCKED_BY_QUOTA':
      return {
        kind: 'BLOCKED_BY_QUOTA',
        message: outcome.message,
        quotaKind: outcome.quotaKind,
      }
    case 'NEEDS_CONFIRMATION':
      return {
        kind: 'NEEDS_CONFIRMATION',
        message: outcome.message,
        confirmPayload: outcome.confirmPayload,
        quickControls: QUICK_CONTROLS_BY_KIND[kind] ?? [
          { label: 'Confirm', value: 'Yes' },
          { label: 'Cancel', value: 'Never mind.' },
        ],
      }
  }
}

export const dispatchLineExecute = async (
  context: LineExecuteContext,
): Promise<LineExecuteDispatchResult> => {
  const intent = detectLineExecuteIntent(context.prompt) ?? detectCatalogExecuteIntent(context)
  if (!intent) return { kind: 'NO_MATCH' }
  const availabilityOutcome = outcomeForCatalogAvailability(intent)
  if (availabilityOutcome) return { kind: 'DISPATCHED', intent, outcome: availabilityOutcome }

  const actionId = LINE_EXECUTE_KIND_TO_ACTION_ID[intent.kind]
  if (!actionId || !getAction(actionId)) {
    return { kind: 'ADAPTER_MISSING', intent }
  }

  try {
    const args = buildActionArgs(intent, context)
    const actionContext = toActionContext(intent, context)
    const actionOutcome = await invokeAction(actionId, args, actionContext)
    const outcome = toLineExecuteOutcome(intent.kind, actionOutcome)
    return { kind: 'DISPATCHED', intent, outcome }
  } catch (error) {
    return {
      kind: 'ADAPTER_ERROR',
      intent,
      error: error instanceof Error ? error.message : 'Unknown action error.',
    }
  }
}

const outcomeForCatalogAvailability = (intent: LineExecuteIntentMatch): LineExecuteOutcome | null => {
  const availability = intent.slots.catalogAvailability
  if (!availability || typeof availability !== 'object') return null
  const state = 'state' in availability ? availability.state : null
  const message = 'message' in availability && typeof availability.message === 'string'
    ? availability.message
    : 'This intent is not available right now.'
  if (state === 'needs_permission') {
    const missingPermissions = 'missingPermissions' in availability && Array.isArray(availability.missingPermissions)
      ? availability.missingPermissions
      : []
    return {
      kind: 'BLOCKED_BY_PERMISSION',
      message,
      missingPermission: (missingPermissions[0] as WorkspaceAction | undefined) ?? 'ai.use',
    }
  }
  if (state === 'needs_target' || state === 'unavailable' || state === 'unsupported') {
    return { kind: 'FAILED', message, recoverable: state !== 'unsupported' }
  }
  return null
}

const detectCatalogExecuteIntent = (context: LineExecuteContext): LineExecuteIntentMatch | null => {
  const catalogMatch = detectWebIntent(context.prompt, context.webIntents ?? WEB_INTENT_CATALOG)
  if (!catalogMatch) return null
  const intent = catalogMatch.intent
  if (!intent?.executeActionKind) return null
  const availability = resolveWebIntentAvailability(intent, {
    canPerform: context.canPerform,
    switchedOffModules: context.switchedOffModules,
  })
  if (availability.state !== 'available') {
    return {
      kind: intent.executeActionKind,
      confidence: Math.min(0.95, 0.55 + catalogMatch.score / 40),
      slots: {
        catalogIntentId: intent.id,
        catalogAvailability: availability,
        targetRoute: intent.targetRef?.route,
        targetRef: intent.targetRef,
      },
      matchedText: catalogMatch.matchedText,
      reason: availability.message,
      catalogIntentId: intent.id,
    }
  }
  return {
    kind: intent.executeActionKind,
    confidence: Math.min(0.95, 0.55 + catalogMatch.score / 40),
    slots: {
      catalogIntentId: intent.id,
      targetRoute: intent.targetRef?.route,
      targetRef: intent.targetRef,
    },
    matchedText: catalogMatch.matchedText,
    reason: catalogMatch.reason,
    catalogIntentId: intent.id,
  }
}
